import { useMemo } from "react";
import { TrendingUp, Calendar } from "lucide-react";
import clsx from "clsx";

import { StatCard } from "./StatCard";
import { SlotMachineCurrency } from "./SlotMachineCurrency";
import { NarrativeCallout } from "./NarrativeCallout";
import { formatCurrency, getCompletedMonthsForComparison } from "./utils";
import { useMoneySaved } from "./MoneySavedContext";
import { useScrollReveal } from "./useScrollReveal";

export const MoneyStatCardGrid = ({
  showNarrative = false,
}: {
  showNarrative?: boolean;
}) => {
  const { monthlySavings } = useMoneySaved();
  const { ref, isVisible } = useScrollReveal(0.2);

  const { current: compareMonth, previous: baseMonth } =
    getCompletedMonthsForComparison(monthlySavings);

  const currentMonthLabel = compareMonth
    ? new Date(compareMonth.month + "-01T00:00:00").toLocaleDateString(
        "en-US",
        {
          month: "long",
          year: "numeric",
        },
      )
    : "";

  const currentMonthShort = compareMonth
    ? new Date(compareMonth.month + "-01T00:00:00").toLocaleDateString(
        "en-US",
        {
          month: "long",
        },
      )
    : "";

  const avgMonthlySavings = useMemo(() => {
    if (monthlySavings.length === 0) return 0;
    const total = monthlySavings.reduce((sum, m) => sum + m.savings, 0);
    return Math.round(total / monthlySavings.length);
  }, [monthlySavings]);

  const savingsChange = useMemo(() => {
    if (!compareMonth || !baseMonth) return null;
    if (baseMonth.savings === 0) return null;
    return Math.round(
      ((compareMonth.savings - baseMonth.savings) / baseMonth.savings) * 100,
    );
  }, [compareMonth, baseMonth]);

  const privacyRatio = useMemo(() => {
    if (!compareMonth || compareMonth.conversions === 0) return 0;
    return Math.round(
      (compareMonth.privacy_conversions / compareMonth.conversions) * 100,
    );
  }, [compareMonth]);

  const narrativeHeadline = useMemo(() => {
    if (!compareMonth) return null;
    if (savingsChange !== null && savingsChange > 0) {
      return (
        <>
          {currentMonthShort} was your{" "}
          <span className="text-blue-600">best month yet.</span>
        </>
      );
    }
    return (
      <>
        Here's how <span className="text-blue-600">{currentMonthShort}</span>{" "}
        breaks down.
      </>
    );
  }, [savingsChange, currentMonthShort, compareMonth]);

  const narrativeSubtext = useMemo(() => {
    if (!compareMonth) return null;
    const savings = compareMonth.savings;
    if (savingsChange !== null && savingsChange > 0 && privacyRatio > 0) {
      return `You saved ${formatCurrency(savings)} this month — ${privacyRatio}% of your conversions were recovered through CAPI.`;
    }
    if (savingsChange !== null && savingsChange > 0) {
      return `You saved ${formatCurrency(savings)} this month alone — ${savingsChange}% more than last month.`;
    }
    if (privacyRatio > 0) {
      return `You saved ${formatCurrency(savings)} this month. ${privacyRatio}% of your conversions were recovered through CAPI.`;
    }
    return `You saved ${formatCurrency(savings)} this month.`;
  }, [savingsChange, compareMonth, privacyRatio]);

  return (
    <div ref={ref} className="flex flex-col gap-8">
      {showNarrative && narrativeHeadline && narrativeSubtext && (
        <NarrativeCallout
          headline={narrativeHeadline}
          subtext={narrativeSubtext}
        />
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div
          className={clsx(
            "transition-all duration-700",
            isVisible ? "animate-slide-in-left" : "opacity-0",
          )}
          style={{ animationDelay: showNarrative ? "400ms" : "0ms" }}
        >
          <StatCard
            icon={Calendar}
            label="This Month"
            value={
              <SlotMachineCurrency
                value={compareMonth?.savings ?? 0}
                trigger={isVisible}
                formatFn={formatCurrency}
              />
            }
            subtext={currentMonthLabel}
            color="blue"
          />
        </div>
        <div
          className={clsx(
            "transition-all duration-700",
            isVisible ? "animate-slide-in-right" : "opacity-0",
          )}
          style={{ animationDelay: showNarrative ? "600ms" : "0ms" }}
        >
          <StatCard
            icon={TrendingUp}
            label="Avg Monthly Savings"
            value={
              <SlotMachineCurrency
                value={avgMonthlySavings}
                trigger={isVisible}
                formatFn={formatCurrency}
              />
            }
            subtext={`Last ${monthlySavings.length} months`}
            color="orange"
          />
        </div>
      </div>
    </div>
  );
};
